import { getSettings } from '../../queries.js';
import { createPatch } from 'rfc6902';
import { uuidv7 } from 'uuidv7';
import { PatientTherapySequenceStatus } from '../../database/schema.js';
import { zql } from '../../schema.js';
import { addDaysUtc, computeCycleDayFromTherapyDate, computeTherapyDateFromCycleDay, daysBetweenTimestamps, EventLogs, getDosingWeight, getTotalAmountOfSubstanceDecimal, getFirstCycleStartDate, } from '../../utils.js';
import { nextLocalPrescriptionCode } from '../patient.mutators.js';
import { addEventLog, createPatchWithOldValues, EventLogResources } from '../util.js';
import { canEditValidatedPrescription } from '../../util/patients-therapies.js';
import { loadPatientProtocolForTherapy, loadTherapyParameterContext } from './add-therapy-parameter.js';
import { getDoseBasis } from './dose-basis.js';
import { assertTotalAmountsNotZero, getMedicineTotalAmount } from './medicine-total-amount.js';
import { medicineLogItems, noteVariant, sequenceLogItems, sequenceNameItems } from './medicine-log-items.js';
import { logDoseLimitConfirmations } from './dose-limit-log.js';
import { medicineUpdateTotalAmounts, removeUnusedMedicines } from './medicine-update-total-amounts.js';
import { itemsDetail } from '../../util/event-log-items.js';
/**
 * The doctor may edit a prescription while it is awaiting their validation or,
 * once validated, until the pharmacist validates it. After pharmacist
 * validation the prescription is locked.
 */
export function canDoctorUpdateTherapy(sequence) {
    const status = sequence.status;
    return (status === PatientTherapySequenceStatus.enum.pending_doctor_validation ||
        status === PatientTherapySequenceStatus.enum.pending_approval ||
        (status === PatientTherapySequenceStatus.enum.validating && !sequence.pharmacistValidatedAt));
}
/**
 * A prescription can be date-shifted until the pharmacy validates it. Once it is pharmacy
 * validated (or any later status), its cycle is considered in progress and it is locked.
 */
export function canShiftTherapySequence(sequence) {
    return canDoctorUpdateTherapy(sequence);
}
function normalizeClinicalValue(value) {
    return value === '' || value === null ? undefined : value;
}
function isSameClinicalValue(a, b) {
    return normalizeClinicalValue(a) === normalizeClinicalValue(b);
}
/** Reports whether a save touches anything other than the therapy note, pharmacy note, carrier solution, volume, volume mode and - unless No Aut Idem fixed the product - the trade name. */
export function hasChangesOutsideValidationExceptions(prev, input, options = {}) {
    if (!isSameClinicalValue(prev.responsibleDoctorId, input.responsibleDoctorId))
        return true;
    if (!isSameClinicalValue(prev.patientProtocolId, input.patientProtocolId))
        return true;
    if (input.fixedDate != null && !isSameClinicalValue(prev.therapyDate, input.fixedDate))
        return true;
    if (input.cycle != null && prev.cycle != null && !isSameClinicalValue(prev.cycle, input.cycle))
        return true;
    if (input.day != null && prev.day != null && !isSameClinicalValue(prev.day, input.day))
        return true;
    if (!isSameClinicalValue(prev.time, input.time))
        return true;
    if (!isSameClinicalValue(prev.applicationFormId, input.applicationFormId))
        return true;
    if (!isSameClinicalValue(prev.duration ?? 0, input.duration))
        return true;
    if (!isSameClinicalValue(prev.note, input.note))
        return true;
    const prevMedicines = prev.medicine ?? [];
    if (input.medicine.length !== prevMedicines.length)
        return true;
    const prevById = new Map(prevMedicines.map(medicine => [medicine.id, medicine]));
    for (const medicine of input.medicine) {
        const prevMedicine = medicine.id ? prevById.get(medicine.id) : undefined;
        if (!prevMedicine)
            return true;
        if (!isSameClinicalValue(prevMedicine.activeSubstanceId, medicine.activeSubstanceId))
            return true;
        // a pharmacist may change the trade name, but not when No Aut Idem fixed the product
        const prescribedNameIsReadOnly = !options.ignorePrescribedName || Boolean(prevMedicine.autIdem);
        if (prescribedNameIsReadOnly && !isSameClinicalValue(prevMedicine.prescribedNameId, medicine.prescribedNameId))
            return true;
        if (!isSameClinicalValue(prevMedicine.calculationMethod, medicine.calculationMethod))
            return true;
        if (!isSameClinicalValue(prevMedicine.regularDose ?? 0, medicine.regularDose))
            return true;
        if (!isSameClinicalValue(prevMedicine.precisionDecimals ?? 2, medicine.precisionDecimals))
            return true;
        if (!isSameClinicalValue(prevMedicine.doseProportion ?? 100, medicine.doseProportion))
            return true;
        if (Boolean(prevMedicine.autIdem) !== Boolean(input.autIdem))
            return true;
    }
    return false;
}
/** A doctor-validated prescription keeps its validation when a save only touches the fields that do not change what the doctor validated, the trade name included unless No Aut Idem fixed the product. */
export function keepsDoctorValidation(prev, input) {
    return !hasChangesOutsideValidationExceptions(prev, input, { ignorePrescribedName: true });
}
/**
 * A save keeps the total amounts and the values they were calculated with when they are the ones a doctor
 * validated: on a doctor-validated prescription, or on a remake still carrying the original's (CY-303),
 * as long as the save does not change what the doctor validated.
 */
export function keepsCalculatedTotals(prev, input) {
    const validatedTotals = prev.status === PatientTherapySequenceStatus.enum.validating || prev.doseCopiedFromSequenceId != null;
    return validatedTotals && keepsDoctorValidation(prev, input);
}
/**
 * Only a doctor may change what the doctor validated. A pharmacist may also change the trade
 * name, carrier solution, volume, volume mode and notes, so those are the only fields not
 * compared here.
 */
export function hasReadOnlyPrescriptionChanges(prev, input) {
    return hasChangesOutsideValidationExceptions(prev, input, { ignorePrescribedName: true });
}
/** Rejects a pharmacist's attempt to change a field a doctor validated. */
export function assertCanEditDoctorValidatedPrescription(prev, input, roles) {
    // only a pharmacist is restricted; a doctor can change every field
    if (!roles.canValidatePharmacist || canEditValidatedPrescription(roles))
        return;
    // only the doctor-validated stage before pharmacy validation is restricted
    if (prev.status !== PatientTherapySequenceStatus.enum.validating || prev.pharmacistValidatedAt)
        return;
    if (hasReadOnlyPrescriptionChanges(prev, input)) {
        throw new Error('Only a doctor can change the doctor, protocol, date, time, dose, application form or duration');
    }
}
/**
 * Builds the event-log payload for a single medicine update. Existing medicines are
 * logged as `update` with a real diff (old vs new); new medicines as `create`.
 */
function buildMedicineLogPayload(prevMedicine, newMedicine) {
    const isUpdate = Boolean(prevMedicine);
    const patches = prevMedicine ? createPatchWithOldValues(prevMedicine, newMedicine) : createPatch({}, newMedicine);
    return { action: isUpdate ? 'update' : 'create', patches };
}
function pushProtocolMedicineOps(tx, params, sequenceOps, logSequenceOps) {
    const { input, prevMedicines, therapySequenceId, therapyDate, now, userId, internalId, context, keepValidatedTotals } = params;
    const medicineIds = [];
    const prevById = new Map(prevMedicines.map(m => [m.id, m]));
    for (const med of input.medicine) {
        const id = med.id || uuidv7();
        medicineIds.push(id);
        const totalAmount = getTotalAmountOfSubstanceDecimal({
            calculationMethod: med.calculationMethod,
            dose: med.regularDose,
            doseProportion: med.doseProportion || 100,
            roundingMode: context.roundingModes.get(med.activeSubstanceId),
            dosePrecision: med.precisionDecimals,
            height: context.characteristics?.height ?? null,
            weight: getDosingWeight(context.characteristics),
            gfr: context.gfrLab?.gfr ?? null,
            bsa: context.characteristics?.bsa ?? null,
        });
        const prevMedicine = prevById.get(id);
        const medicineData = {
            id,
            therapySequenceId,
            patientId: input.patientId,
            therapyDate,
            activeSubstanceId: med.activeSubstanceId,
            calculationMethod: med.calculationMethod,
            doseProportion: med.doseProportion ?? undefined,
            prescribedNameId: med.prescribedNameId,
            autIdem: input.autIdem ?? false,
            regularDose: med.regularDose,
            precisionDecimals: med.precisionDecimals,
            totalAmount: getMedicineTotalAmount(totalAmount, prevMedicine, Boolean(keepValidatedTotals)),
            updatedAt: now,
            updatedBy: userId,
        };
        assertTotalAmountsNotZero(tx, [medicineData.totalAmount]);
        sequenceOps.push(() => tx.mutate.patientTherapySequenceMedicine.upsert(medicineData));
        if (tx.location === 'server') {
            const { action, patches } = buildMedicineLogPayload(prevMedicine, medicineData);
            logSequenceOps.push(async () => addEventLog(tx, {
                id: uuidv7(),
                resource: EventLogResources.patientTherapySequenceMedicine,
                resourceId: medicineData.id,
                action,
                domain: 'prescription',
                event: prevMedicine
                    ? EventLogs.PATIENT_THERAPY_SEQUENCE_MEDICINE_PROTOCOL_UPDATED
                    : EventLogs.PATIENT_THERAPY_SEQUENCE_MEDICINE_PROTOCOL_ADDED,
                patches,
                performedBy: userId,
                performedAt: now,
                details: {
                    patientId: input.patientId,
                    patientName: context.patient?.fullName ?? '',
                    prescribedNameId: med.prescribedNameId ?? '',
                    activeSubstanceId: med.activeSubstanceId ?? '',
                    therapyInternalId: internalId ?? '',
                    ...itemsDetail(await medicineLogItems(tx, prevMedicine, medicineData)),
                },
            }));
        }
    }
    return medicineIds;
}
export async function updateFixedDateTherapyParameter(tx, input, userId, roles) {
    const now = Date.now();
    const prev = await tx.run(zql.patientTherapySequence
        .where('id', '=', input.id)
        .related('medicine', q => q.where('deletedAt', 'IS', null))
        .one());
    if (!prev) {
        throw new Error(`Patient Therapy sequence not found: ${input.id}`);
    }
    assertCanEditDoctorValidatedPrescription(prev, input, roles);
    if (!canDoctorUpdateTherapy(prev)) {
        throw new Error('Cannot update a therapy sequence after pharmacist validation');
    }
    if (!input.fixedDate) {
        throw new Error('Either patientProtocolId or fixedDate must be provided');
    }
    const context = await loadTherapyParameterContext(tx, input.patientId, input.medicine.map(med => med.activeSubstanceId));
    const { patient } = context;
    const therapyDate = input.fixedDate;
    let internalId = prev.internalId;
    if (prev.therapyDate !== therapyDate || !prev.internalId) {
        const prescriptionDate = new Date(therapyDate);
        const internalIdStructure = await nextLocalPrescriptionCode(tx, `PR-${prescriptionDate.getFullYear()}${String(prescriptionDate.getMonth() + 1).padStart(2, '0')}${String(prescriptionDate.getDate()).padStart(2, '0')}`);
        internalId = `${internalIdStructure.prefixDash}${internalIdStructure.maxNum + 1}`;
    }
    // Editing a doctor-validated prescription (status `validating`) reverses the validation,
    // except when the save only touches the notes, carrier, volume or volume mode.
    // A remade prescription keeps the dose copied from the original the same way (CY-303).
    const keepsValidatedTotals = keepsCalculatedTotals(prev, input);
    const clearsDoctorValidation = prev.status === PatientTherapySequenceStatus.enum.validating && !keepsDoctorValidation(prev, input);
    const sequenceUpdate = {
        id: input.id,
        note: input.note,
        therapyNote: input.therapyNote,
        pharmacyNote: input.pharmacyNote,
        time: input.time,
        internalId,
        carrierSolutionId: input.carrierSolutionId,
        applicationFormId: input.applicationFormId,
        duration: input.duration,
        volume: input.volume,
        volumeMode: input.volumeMode,
        responsibleDoctorId: input.responsibleDoctorId,
        updatedAt: now,
        updatedBy: userId,
        therapyDate,
        ...(!keepsValidatedTotals && {
            ...getDoseBasis(context.characteristics, context.gfrLab),
            doseCopiedFromSequenceId: null,
        }),
        ...(clearsDoctorValidation && {
            status: PatientTherapySequenceStatus.enum.pending_doctor_validation,
            doctorValidatedBy: null,
            doctorValidatedAt: null,
        }),
    };
    const sequenceOps = [() => tx.mutate.patientTherapySequence.update(sequenceUpdate)];
    const logSequenceOps = [];
    if (tx.location === 'server') {
        const patches = createPatchWithOldValues({ ...prev, medicine: [] }, sequenceUpdate);
        const items = [...sequenceLogItems(prev, sequenceUpdate), ...(await sequenceNameItems(tx, prev, sequenceUpdate))];
        logSequenceOps.push(() => addEventLog(tx, {
            id: uuidv7(),
            resource: EventLogResources.patientTherapySequence,
            resourceId: input.id,
            action: 'update',
            domain: 'prescription',
            event: EventLogs.PATIENT_THERAPY_SEQUENCE_UPDATED,
            patches,
            performedBy: userId,
            performedAt: now,
            details: {
                id: internalId,
                patientName: patient?.fullName,
                patientId: input.patientId,
                therapyDate: therapyDate?.toString(),
                time: sequenceUpdate.time,
                variant: noteVariant(items),
                ...itemsDetail(items),
            },
        }));
    }
    const medicineIds = [];
    const prevById = new Map(prev.medicine.map(m => [m.id, m]));
    for (const med of input.medicine) {
        const id = med.id || uuidv7();
        medicineIds.push(id);
        const isUpdate = Boolean(med.id);
        const totalAmount = getTotalAmountOfSubstanceDecimal({
            calculationMethod: med.calculationMethod,
            dose: med.regularDose,
            doseProportion: med.doseProportion || 100,
            roundingMode: context.roundingModes.get(med.activeSubstanceId),
            dosePrecision: med.precisionDecimals,
            height: context.characteristics?.height ?? null,
            weight: getDosingWeight(context.characteristics),
            gfr: context.gfrLab?.gfr ?? null,
            bsa: context.characteristics?.bsa ?? null,
        });
        const medicineData = {
            id,
            therapySequenceId: input.id,
            patientId: input.patientId,
            therapyDate,
            activeSubstanceId: med.activeSubstanceId,
            prescribedNameId: med.prescribedNameId,
            autIdem: input.autIdem ?? false,
            regularDose: med.regularDose,
            precisionDecimals: med.precisionDecimals,
            doseProportion: med.doseProportion ?? undefined,
            calculationMethod: med.calculationMethod,
            totalAmount: getMedicineTotalAmount(totalAmount, prevById.get(id), keepsValidatedTotals),
            ...(isUpdate ? { updatedAt: now, updatedBy: userId } : { createdAt: now, createdBy: userId }),
        };
        assertTotalAmountsNotZero(tx, [medicineData.totalAmount]);
        sequenceOps.push(() => tx.mutate.patientTherapySequenceMedicine.upsert(medicineData));
        if (tx.location === 'server') {
            const prevMedicine = prevById.get(id);
            const { action, patches } = buildMedicineLogPayload(prevMedicine, medicineData);
            logSequenceOps.push(async () => addEventLog(tx, {
                id: uuidv7(),
                resource: EventLogResources.patientTherapySequenceMedicine,
                resourceId: medicineData.id,
                action,
                domain: 'prescription',
                event: prevMedicine
                    ? EventLogs.PATIENT_THERAPY_SEQUENCE_MEDICINE_UPDATED
                    : EventLogs.PATIENT_THERAPY_SEQUENCE_MEDICINE_ADDED,
                patches,
                performedBy: userId,
                performedAt: now,
                details: {
                    patientId: input.patientId,
                    patientName: patient?.fullName ?? '',
                    prescribedNameId: med.prescribedNameId ?? '',
                    activeSubstanceId: med.activeSubstanceId ?? '',
                    therapyInternalId: sequenceUpdate.internalId,
                    ...itemsDetail(await medicineLogItems(tx, prevMedicine, medicineData)),
                },
            }));
        }
    }
    await Promise.all(sequenceOps.map(op => op()));
    await Promise.all(logSequenceOps.map(op => op()));
    const prevMedicineIds = prev.medicine.map(m => m.id);
    await removeUnusedMedicines(tx, userId, prevMedicineIds, medicineIds);
    await medicineUpdateTotalAmounts(tx, input.patientId, [input.id]);
    await logSavedDoseLimitConfirmations(tx, input, userId);
}
export async function updateProtocolTherapyParameter(tx, input, userId, roles) {
    const prev = await loadPrevTherapySequenceWithMedicine(tx, input.id);
    if (!prev) {
        throw new Error(`Patient Therapy sequence not found: ${input.id}`);
    }
    assertCanEditDoctorValidatedPrescription(prev, input, roles);
    if (!canDoctorUpdateTherapy(prev)) {
        throw new Error('Cannot update a therapy sequence after pharmacist validation');
    }
    if (!prev.cycle || !prev.patientProtocol || !prev.protocol) {
        return attachTherapyParameterToProtocol(tx, input, userId, prev);
    }
    const now = Date.now();
    const inputDay = input.day ?? -1;
    if (!Number.isFinite(inputDay) || inputDay < 0) {
        throw new Error('Day must be provided when patientProtocolId is provided');
    }
    const { daysRepetition } = prev.protocol;
    const totalCycles = prev.patientProtocol.cycles ?? prev.protocol.totalCycles;
    const requestedCycle = input.cycle ?? prev.cycle;
    if (requestedCycle < 1 || requestedCycle > totalCycles) {
        throw new Error(`Cycle must be between 1 and ${totalCycles}`);
    }
    const context = await loadTherapyParameterContext(tx, input.patientId, input.medicine.map(med => med.activeSubstanceId));
    const { patient } = context;
    const settings = await getSettings(tx);
    const isZeroDayProtocol = settings?.dayZeroProtocol;
    // Cycles can be individually moved (Edit protocol dialog), so anchor to the cycle's own
    // recorded start date (cycleStartDates) instead of assuming an even day-repetition spacing.
    const therapyDate = computeTherapyDateFromCycleDay(prev.patientProtocol, requestedCycle, inputDay, daysRepetition, isZeroDayProtocol);
    // Editing a doctor-validated prescription (status `validating`) reverses the validation,
    // except when the save only touches the notes, carrier, volume or volume mode.
    // A remade prescription keeps the dose copied from the original the same way (CY-303).
    const keepsValidatedTotals = keepsCalculatedTotals(prev, input);
    const clearsDoctorValidation = prev.status === PatientTherapySequenceStatus.enum.validating && !keepsDoctorValidation(prev, input);
    const sequenceUpdate = {
        id: input.id,
        patientId: input.patientId,
        protocolId: prev.patientProtocol.protocolId,
        patientProtocolId: prev.patientProtocol.id,
        therapyDate,
        day: inputDay,
        cycle: requestedCycle,
        isNote: false,
        note: input.note,
        therapyNote: input.therapyNote,
        pharmacyNote: input.pharmacyNote,
        time: input.time,
        carrierSolutionId: input.carrierSolutionId,
        applicationFormId: input.applicationFormId,
        duration: input.duration,
        volume: input.volume,
        volumeMode: input.volumeMode,
        responsibleDoctorId: input.responsibleDoctorId,
        updatedAt: now,
        updatedBy: userId,
        ...(!keepsValidatedTotals && {
            ...getDoseBasis(context.characteristics, context.gfrLab),
            doseCopiedFromSequenceId: null,
        }),
        ...(clearsDoctorValidation && {
            status: PatientTherapySequenceStatus.enum.pending_doctor_validation,
            doctorValidatedBy: null,
            doctorValidatedAt: null,
        }),
    };
    const sequenceOps = [() => tx.mutate.patientTherapySequence.update(sequenceUpdate)];
    const logSequenceOps = [];
    if (tx.location === 'server') {
        const patches = createPatchWithOldValues(prev, sequenceUpdate);
        const items = [...sequenceLogItems(prev, sequenceUpdate), ...(await sequenceNameItems(tx, prev, sequenceUpdate))];
        logSequenceOps.push(() => addEventLog(tx, {
            id: uuidv7(),
            resource: EventLogResources.patientTherapySequence,
            resourceId: input.id,
            action: 'update',
            domain: 'prescription',
            event: EventLogs.PATIENT_THERAPY_SEQUENCE_PROTOCOL_UPDATED,
            patches,
            performedBy: userId,
            performedAt: now,
            details: {
                therapyInternalId: prev.internalId,
                patientName: patient?.fullName ?? '',
                patientId: input.patientId,
                variant: noteVariant(items),
                ...itemsDetail(items),
            },
        }));
    }
    const medicineIds = pushProtocolMedicineOps(tx, {
        input,
        prevMedicines: prev.medicine,
        therapySequenceId: input.id,
        therapyDate,
        now,
        userId,
        internalId: prev.internalId,
        context,
        keepValidatedTotals: keepsValidatedTotals,
    }, sequenceOps, logSequenceOps);
    await Promise.all(sequenceOps.map(op => op()));
    await Promise.all(logSequenceOps.map(op => op()));
    const prevMedicineIds = prev.medicine.map(m => m.id);
    await removeUnusedMedicines(tx, userId, prevMedicineIds, medicineIds);
    if (input.shiftFutureSequences && prev.patientProtocolId && prev.patientProtocol.startDate) {
        // Everything scheduled for this protocol assignment on or after the day being moved -
        // any drug, not just this one's own future cycles - moves by the same number of days,
        // since it represents the same missed visit / rescheduled treatment day onward.
        const deltaDays = daysBetweenTimestamps(prev.therapyDate, therapyDate);
        const protocolSequences = await tx.run(zql.patientTherapySequence.where('patientProtocolId', '=', prev.patientProtocolId).where('deletedAt', 'IS', null));
        const eligibleSiblings = protocolSequences.filter(seq => seq.id !== input.id && seq.therapyDate >= prev.therapyDate && canShiftTherapySequence(seq));
        const therapySequenceIds = eligibleSiblings.map(seq => seq.id);
        const siblingMedicines = therapySequenceIds.length
            ? await tx.run(zql.patientTherapySequenceMedicine
                .where('therapySequenceId', 'IN', therapySequenceIds)
                .where('deletedAt', 'IS', null))
            : [];
        const siblingOps = [];
        for (const seq of eligibleSiblings) {
            const siblingTherapyDate = addDaysUtc(seq.therapyDate, deltaDays);
            const { cycle: siblingCycle, day: siblingDay } = computeCycleDayFromTherapyDate(prev.patientProtocol, siblingTherapyDate, daysRepetition, isZeroDayProtocol);
            const wasSiblingValidated = seq.status === PatientTherapySequenceStatus.enum.validating;
            siblingOps.push(() => tx.mutate.patientTherapySequence.update({
                id: seq.id,
                day: siblingDay,
                cycle: siblingCycle,
                therapyDate: siblingTherapyDate,
                updatedAt: now,
                updatedBy: userId,
                ...(wasSiblingValidated && {
                    status: PatientTherapySequenceStatus.enum.pending_doctor_validation,
                    doctorValidatedBy: null,
                    doctorValidatedAt: null,
                    pharmacistValidatedAt: null,
                }),
            }));
            const sequenceMedicine = siblingMedicines.filter(med => med.therapySequenceId === seq.id);
            for (const med of sequenceMedicine) {
                siblingOps.push(() => tx.mutate.patientTherapySequenceMedicine.update({
                    id: med.id,
                    therapyDate: siblingTherapyDate,
                    updatedAt: now,
                    updatedBy: userId,
                }));
            }
        }
        await Promise.all(siblingOps.map(op => op()));
    }
    await medicineUpdateTotalAmounts(tx, input.patientId, [input.id]);
    await logSavedDoseLimitConfirmations(tx, input, userId);
}
async function attachTherapyParameterToProtocol(tx, input, userId, prev) {
    if (!input.patientProtocolId) {
        throw new Error('patientProtocolId is required');
    }
    const inputDay = input.day ?? -1;
    if (!Number.isFinite(inputDay) || inputDay < 0) {
        throw new Error('Day must be provided when patientProtocolId is provided');
    }
    const patientProtocol = await loadPatientProtocolForTherapy(tx, input.patientProtocolId, input.patientId);
    const { daysRepetition } = patientProtocol.protocol;
    const totalCycles = patientProtocol.cycles ?? patientProtocol.protocol.totalCycles;
    const requestedCycle = input.cycle ?? 1;
    if (requestedCycle < 1 || requestedCycle > totalCycles) {
        throw new Error(`Cycle must be between 1 and ${totalCycles}`);
    }
    const now = Date.now();
    const context = await loadTherapyParameterContext(tx, input.patientId, input.medicine.map(med => med.activeSubstanceId));
    const { patient } = context;
    const settings = await getSettings(tx);
    const isZeroDayProtocol = settings?.dayZeroProtocol;
    // Cycles can be individually moved (Edit protocol dialog), so anchor to the cycle's own
    // recorded start date (cycleStartDates) instead of assuming an even day-repetition spacing.
    const therapyDate = computeTherapyDateFromCycleDay(patientProtocol, requestedCycle, inputDay, daysRepetition, isZeroDayProtocol);
    if (therapyDate < getFirstCycleStartDate(patientProtocol)) {
        throw new Error('Date cannot be before the first cycle start date');
    }
    const sequenceUpdate = {
        id: input.id,
        patientId: input.patientId,
        protocolId: patientProtocol.protocolId,
        patientProtocolId: patientProtocol.id,
        therapyDate,
        day: input.day,
        cycle: requestedCycle,
        status: PatientTherapySequenceStatus.enum.pending_doctor_validation,
        ...(prev.status === PatientTherapySequenceStatus.enum.validating && {
            doctorValidatedBy: null,
            doctorValidatedAt: null,
        }),
        isNote: false,
        note: input.note,
        therapyNote: input.therapyNote,
        pharmacyNote: input.pharmacyNote,
        time: input.time,
        carrierSolutionId: input.carrierSolutionId,
        applicationFormId: input.applicationFormId,
        duration: input.duration,
        volume: input.volume,
        volumeMode: input.volumeMode,
        responsibleDoctorId: input.responsibleDoctorId,
        updatedAt: now,
        updatedBy: userId,
        ...getDoseBasis(context.characteristics, context.gfrLab),
        doseCopiedFromSequenceId: null,
    };
    const sequenceOps = [() => tx.mutate.patientTherapySequence.update(sequenceUpdate)];
    const logSequenceOps = [];
    if (tx.location === 'server') {
        const patches = createPatchWithOldValues(prev, sequenceUpdate);
        const items = [...sequenceLogItems(prev, sequenceUpdate), ...(await sequenceNameItems(tx, prev, sequenceUpdate))];
        logSequenceOps.push(() => addEventLog(tx, {
            id: uuidv7(),
            resource: EventLogResources.patientTherapySequence,
            resourceId: input.id,
            action: 'update',
            domain: 'prescription',
            event: EventLogs.PATIENT_THERAPY_SEQUENCE_PROTOCOL_UPDATED,
            patches,
            performedBy: userId,
            performedAt: now,
            details: {
                therapyInternalId: prev.internalId,
                patientName: patient?.fullName ?? '',
                patientId: input.patientId,
                variant: noteVariant(items),
                ...itemsDetail(items),
            },
        }));
    }
    const medicineIds = pushProtocolMedicineOps(tx, {
        input,
        prevMedicines: prev.medicine,
        therapySequenceId: input.id,
        therapyDate,
        now,
        userId,
        internalId: prev.internalId,
        context,
    }, sequenceOps, logSequenceOps);
    await Promise.all(sequenceOps.map(op => op()));
    await Promise.all(logSequenceOps.map(op => op()));
    const prevMedicineIds = prev.medicine.map(m => m.id);
    await removeUnusedMedicines(tx, userId, prevMedicineIds, medicineIds);
    await medicineUpdateTotalAmounts(tx, input.patientId, [input.id]);
    await logSavedDoseLimitConfirmations(tx, input, userId);
}
/** Dose limit confirmations of an edited prescription, with the totals as saved after recalculation. */
async function logSavedDoseLimitConfirmations(tx, input, userId) {
    if (tx.location !== 'server' || !input.doseLimitConfirmed?.length)
        return;
    const sequence = await tx.run(zql.patientTherapySequence
        .where('id', '=', input.id)
        .related('medicine', q => q.where('deletedAt', 'IS', null))
        .one());
    await logDoseLimitConfirmations(tx, {
        confirmed: input.doseLimitConfirmed,
        medicines: sequence?.medicine ?? [],
        therapyInternalId: sequence?.internalId,
        userId,
        now: Date.now(),
    });
}
function loadPrevTherapySequenceWithMedicine(tx, id) {
    return tx.run(zql.patientTherapySequence
        .where('id', '=', id)
        .related('medicine', q => q.where('deletedAt', 'IS', null))
        .related('protocol')
        .related('patientProtocol')
        .one());
}
//# sourceMappingURL=update-therapy-parameter.js.map